// src/components/modals/DeletePurchaseOrderModal.jsx
import { useState } from 'react'
import { Button } from '../ui/Button'
import { X, AlertTriangle, Loader2, ShoppingCart, Trash2 } from 'lucide-react'
import PurchaseOrderService from '../../services/purchaseOrderService'
import { PurchaseOrderUtils } from '../../utils/purchaseOrderUtils'

const DeletePurchaseOrderModal = ({ isOpen, onClose, purchaseOrder, onOrderDeleted }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  if (!isOpen || !purchaseOrder) return null

  const handleDelete = async () => {
    try {
      setLoading(true)
      setError('')
      
      console.log('🗑️ Soft deleting purchase order & related GRNs:', purchaseOrder._id)
      
      const response = await PurchaseOrderService.deletePurchaseOrder(purchaseOrder._id)
      
      if (response?.success || response?.status === 'success') {
        console.log('✅ Purchase order and related GRNs deleted successfully')
        onOrderDeleted?.(purchaseOrder._id)
        onClose()
      } else {
        throw new Error(response?.message || 'Failed to delete purchase order')
      }
    } catch (error) {
      console.error('❌ Error deleting purchase order:', error)
      setError(error.message || 'Failed to delete purchase order')
    } finally {
      setLoading(false)
    }
  }

  const statusConfig = PurchaseOrderUtils.getStatusConfig(purchaseOrder.status)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-gray-100">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-red-50/50">
          <div className="flex items-center space-x-3">
            <div className="bg-red-100 p-2.5 rounded-xl text-red-600">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Delete Purchase Order</h2>
              <p className="text-xs text-red-700 font-medium">Soft delete PO & related GRNs</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
            disabled={loading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-center">
            <div className="bg-red-100 p-4 rounded-full text-red-600 ring-8 ring-red-50">
              <Trash2 className="h-8 w-8" />
            </div>
          </div>

          <div className="text-center">
            <h3 className="text-base font-bold text-gray-900">
              Are you sure you want to delete PO <span className="font-mono text-red-600">{purchaseOrder.poNumber}</span>?
            </h3>
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl text-left text-xs text-red-800 space-y-1">
              <p className="font-bold flex items-center">
                <AlertTriangle className="h-3.5 w-3.5 text-red-600 mr-1 flex-shrink-0" />
                Cascade Soft Delete Warning:
              </p>
              <p className="text-red-700 leading-relaxed">
                This Purchase Order and any <strong>associated Goods Received Notes (GRN)</strong> will be tagged as <span className="font-bold underline">DELETED</span> in the system. Deleted data will be excluded from all stock balances, GRN payments, and financial P&L calculations.
              </p>
            </div>
          </div>

          {/* Purchase Order Details Card */}
          <div className="bg-gray-50 rounded-xl p-4 text-xs space-y-2 border border-gray-200">
            <div className="flex justify-between">
              <span className="font-medium text-gray-500">PO Number:</span>
              <span className="text-gray-900 font-mono font-bold">{purchaseOrder.poNumber || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-gray-500">Branch:</span>
              <span className="text-gray-900 font-semibold">{purchaseOrder.requestingBranch?.name || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-gray-500">{purchaseOrder.supplierType === 'supplier' ? 'Supplier' : 'Warehouse'}:</span>
              <span className="text-gray-900 font-semibold">
                {purchaseOrder.supplier?.name || purchaseOrder.warehouse?.name || 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-gray-500">Order Date:</span>
              <span className="text-gray-900">
                {PurchaseOrderUtils.formatDate(purchaseOrder.orderDate)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-500">Current Status:</span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold ${statusConfig.bgColor} ${statusConfig.textColor}`}>
                {statusConfig.label}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-gray-500">Total Items:</span>
              <span className="text-gray-900 font-bold">{purchaseOrder.orderItems?.length || 0}</span>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-100 border border-red-300 rounded-xl text-xs text-red-700 font-semibold">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="rounded-xl text-xs font-semibold"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleDelete}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold flex items-center space-x-2 px-4 shadow-sm"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              <span>{loading ? 'Deleting PO & GRNs...' : 'Confirm Soft Delete'}</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DeletePurchaseOrderModal